import { privacySections } from "@/pages/privacyContent";
import useSeo from "@/hooks/useSeo";

export default function Privacy() {
  useSeo({
    title: "Privacy Policy",
    description:
      "Dietico Privacy Policy v1.0 issued by Mesron Healthcare LLP under the Digital Personal Data Protection Act 2023, IT Act 2000 and allied Indian healthcare frameworks.",
    path: "/privacy",
  });
  return (
    <div data-testid="privacy-page">
      {/* Header */}
      <section className="mx-auto max-w-7xl px-6 pt-16 pb-16 md:pt-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <p className="font-mono text-[11px] uppercase tracking-widest text-secondary mb-6">
              Privacy Policy · Version 1.0 · Effective 30 June 2026
            </p>
            <h1 className="text-5xl md:text-7xl font-light tracking-tightest leading-[0.95] text-foreground">
              How Dietico handles your data.
            </h1>
            <p className="mt-8 max-w-2xl text-lg text-secondary leading-relaxed">
              Issued by Mesron Healthcare LLP under the Digital Personal Data
              Protection Act 2023, the IT Act 2000, and allied Indian
              healthcare frameworks.
            </p>
          </div>
          <aside className="lg:col-span-4">
            <div className="rounded-2xl border border-border bg-secondary p-6">
              <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-3">
                Data Fiduciary
              </p>
              <p className="text-foreground text-lg font-medium tracking-tight">
                Mesron Healthcare LLP
              </p>
              <div className="mt-3 space-y-1 text-sm text-secondary">
                <p>LLPIN · ACD-8580</p>
                <p>Incorporated · 19 Nov 2023</p>
                <p>Grievance Officer · Dr. Indranil Chatterjee</p>
                <p>
                  <a
                    href="mailto:admin@dietico.co.in"
                    className="text-foreground hover:opacity-80"
                    data-testid="privacy-email"
                  >
                    admin@dietico.co.in
                  </a>
                </p>
                <p>
                  <a
                    href="tel:+919002296906"
                    className="text-foreground hover:opacity-80"
                    data-testid="privacy-phone"
                  >
                    +91 9002296906
                  </a>
                </p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Body — two column with sticky TOC */}
      <section className="mx-auto max-w-7xl px-6 pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <nav
            data-testid="privacy-toc"
            className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start"
            aria-label="On this page"
          >
            <p className="font-mono text-[10px] uppercase tracking-widest text-secondary mb-4">
              On this page
            </p>
            <ol className="space-y-2 text-sm">
              {privacySections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-secondary hover:text-foreground transition-colors flex gap-3"
                  >
                    <span className="font-mono text-[10px] pt-1">{s.number}</span>
                    <span>{s.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="lg:col-span-9 space-y-14">
            {privacySections.map((s) => (
              <article
                key={s.id}
                id={s.id}
                data-testid={`section-${s.id}`}
                className="scroll-mt-28"
              >
                <div className="flex items-baseline gap-4 mb-4">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-secondary">
                    {s.number}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                </div>
                <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground mb-6 max-w-3xl">
                  {s.title}
                </h2>
                <div className="space-y-5 max-w-3xl text-[15px] md:text-base text-secondary leading-[1.75]">
                  {s.paragraphs.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
